import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Edit, User, Mail, MapPin, Calendar, DollarSign } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { TutorExtendedProfileData, TutorSectionType } from "src/interfaces/TutorProfile";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "src/components/ui/Tooltip";

interface TutorGeneralInformationSectionProps {
  profileData: TutorExtendedProfileData;
  editData: Partial<TutorExtendedProfileData>;
  activeSectionEdit: TutorSectionType | null;
  setActiveSectionEdit: (section: TutorSectionType | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<TutorExtendedProfileData>) => Promise<void>;
  isModified?: boolean;
}

const TutorGeneralInformationSection: React.FC<TutorGeneralInformationSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile,
  isModified = false
}) => {
  return (
    <>
      {/* General Information Section */}
      <Card className={isModified ? "ring-2 ring-blue-200 bg-blue-50/30" : ""}>
        <CardHeader className="relative pb-2">
          <CardTitle>General Information</CardTitle>
          <div className="absolute top-4 right-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 p-0 rounded-full hover:bg-gray-100"
                    onClick={() => {
                      updateEditData("firstName", profileData.firstName || "");
                      updateEditData("lastName", profileData.lastName || "");
                      updateEditData("location", profileData.location || "");
                      updateEditData("date_of_birth", profileData.date_of_birth || null);
                      updateEditData("hourly_rate", profileData.hourly_rate || null);
                      updateEditData("bio", profileData.bio || "");
                      setActiveSectionEdit('general');
                    }}
                    aria-label="Edit General Information"
                  >
                    <Edit className="h-4 w-4 text-gray-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit General Info</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow min-h-[72px]">
              <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <User className="h-6 w-6 text-blue-600" />
              </span>
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Full Name</div>
                <h2 className="text-lg font-semibold mb-1 text-gray-900">{profileData.firstName} {profileData.lastName}</h2>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow min-h-[72px]">
              <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-purple-100">
                <Mail className="h-6 w-6 text-purple-600" />
              </span>
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Email</div>
                <h2 className="text-lg font-semibold mb-1 text-gray-900 break-all">{profileData.email}</h2>
              </div>
            </div>

            {/* Location (optional) */}
            {profileData.location && (
              <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow min-h-[72px]">
                <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <MapPin className="h-6 w-6 text-green-600" />
                </span>
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Location</div>
                  <div className="text-lg font-bold text-gray-900">{profileData.location}</div>
                </div>
              </div>
            )}

            {/* Date of Birth (optional) */}
            {profileData.date_of_birth && (
              <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow min-h-[72px]">
                <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                  <Calendar className="h-6 w-6 text-yellow-600" />
                </span>
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Date of Birth</div>
                  <div className="text-lg font-bold text-gray-900">
                    {new Date(profileData.date_of_birth).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}

            {/* Hourly Rate (optional) */}
            {profileData.hourly_rate && (
              <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow min-h-[72px]">
                <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-pink-100">
                  <DollarSign className="h-6 w-6 text-pink-600" />
                </span>
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Hourly Rate</div>
                  <h2 className="text-lg font-semibold mb-1 text-gray-900">${profileData.hourly_rate}/hr</h2>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* General Information Edit Modal */}
      <Modal
        isOpen={activeSectionEdit === 'general'}
        onClose={() => setActiveSectionEdit(null)}
        title="Edit General Information"
      >
        <div className="space-y-4">
          <FormInput
            label="First Name"
            id="firstName"
            value={editData.firstName || ""}
            onChange={(e) => updateEditData("firstName", e.target.value)}
            helpText="Your first name as it will appear on your profile"
          />

          <FormInput
            label="Last Name"
            id="lastName"
            value={editData.lastName || ""}
            onChange={(e) => updateEditData("lastName", e.target.value)}
            helpText="Your last name as it will appear on your profile"
          />

          <FormInput
            label="Location"
            id="location"
            value={editData.location || ""}
            onChange={(e) => updateEditData("location", e.target.value)}
            placeholder="City, Country"
            helpText="Your current city and country of residence"
          />

          <FormInput
            label="Date of Birth"
            id="date_of_birth"
            type="date"
            value={editData.date_of_birth ? new Date(editData.date_of_birth).toISOString().split('T')[0] : ""}
            onChange={(e) => updateEditData("date_of_birth", e.target.value ? new Date(e.target.value) : null)}
            helpText="Your date of birth"
          />

          <FormInput
            label="Hourly Rate"
            id="hourly_rate"
            type="number"
            value={editData.hourly_rate?.toString() || ""}
            onChange={(e) => updateEditData("hourly_rate", e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="50"
            helpText="Your hourly rate in USD"
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)}>
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TutorGeneralInformationSection;